// @ts-nocheck
import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo, useContext } from 'react'
import { useSetAtom } from 'jotai'
import { permissionsVersionAtom } from '@/utils/atoms'
import SubscriptionContext from '@/utils/subscription-context'
import Storage from '@/utils/storage'
import * as Enums from '@/utils/enums'
import BillingService from '@/services/billing-service'
import useBreakpoint from '@/hooks/useBreakpoint'
import Constants from '@/utils/constants'
import ConfirmAction from '@/components/modals/confirm-action'
import Helper from '@/utils/helper'
import BusyIndicator from '@/components/busy-indicator'
import BusyIndicatorContext from '@/utils/busy-indicator-context'
import HubspotContext from '@/utils/contexts/hubspot-context'
import PageContext from '@/utils/page-context'
import { colors, layout } from '@/theme/index'
import Time from '@/utils/time'
import { themeOptions } from '@/theme/material/material-ui'
import authenticationService from '@/services/authentication/authentication-service'
import useRefState from '@/hooks/useRefState'
import employeeService from '@/services/employee/employee-service'
import { refreshPlatformSession } from '@/services/platform-token-refresh'
import { isSessionDenied } from '@/services/platform-auth'
import AuthProvider from '@/PageComponents/Auth/AuthProvider'
import { ThemeProvider } from '@mui/material'
import PS from '@/services/permission/permission-service'
import { identifyClarityUser, identifySentryUser, getRequiredPermissions, logout } from '@/utils/auth'
import hubspotService from '@/services/hubspot/hubspot-service'
import UserConfigService from '@/services/option/user-config-service'
import constants from '@/utils/constants'
import moment from 'moment'
import { useMediaQuery } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'

const maintenanceUrlBase = `https://scgeneraltechstorage.blob.core.windows.net/maintenance/<host>.json`

const getPageTitle = (path: any) => {
  if (!path || path === '/') return 'ServCraft'
  const p = path.toLowerCase()
  if (p.startsWith('/job')) return 'Jobs | ServCraft'
  if (p.startsWith('/appointment')) return 'Appointments | ServCraft'
  if (p.startsWith('/query')) return 'Queries | ServCraft'
  if (p.startsWith('/quote')) return 'Quotes | ServCraft'
  if (p.startsWith('/invoice')) return 'Invoices | ServCraft'
  if (p.startsWith('/purchase')) return 'Purchases | ServCraft'
  if (p.startsWith('/customer')) return 'Customers | ServCraft'
  if (p.startsWith('/inventory')) return 'Inventory | ServCraft'
  if (p.startsWith('/report')) return 'Reports | ServCraft'
  if (p.startsWith('/message')) return 'Messages | ServCraft'
  if (p.startsWith('/settings')) return 'Settings | ServCraft'
  return 'ServCraft'
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ location }) => {
    const token = Storage.getCookie(Enums.Cookie.token);
    if (!token) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()

  // HTTPS enforcement
  useEffect(() => {
    if (import.meta.env.MODE === 'production' && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      window.location.href = window.location.href.replace('http:', 'https:')
    }
  }, [])

  // Page title
  useEffect(() => {
    document.title = getPageTitle(pathname)
  }, [pathname])

  // Detect external session invalidation (e.g. DevTools "Clear site data").
  // Storage events are not fired by browser-level clearing, so we poll the
  // token cookie every 3 s. If it disappears while we are on an authenticated
  // route, treat it as a logout so the app doesn't silently stay broken.
  useEffect(() => {
    const id = setInterval(() => {
      if (!Storage.getCookie(Enums.Cookie.token)) {
        logout(false, '', 'token-missing-from-storage')
      }
    }, 3_000)
    return () => clearInterval(id)
  }, [])

  const [usingNewComponents, setUsingNewComponents] = useState(false)
const [busyIndicatorText, setBusyIndicatorText] = useState<any>(null)

  const ref = useRef<any>()
  const runningTimerWatchdogMinutes = 1
  const [lastKnownPosition, setLastKnownPosition] = useState({ x: -1, y: -1, stamp: new Date().valueOf() })

  const [versionUpgradeAlerted, setVersionUpgradeAlerted] = useState(false)
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions())

  const refTimer = useRef<any>(null)
  const { subscriptionInfo, setSubscriptionInfo, getSubscriptionInfo } = useContext(SubscriptionContext)
  // Read by the long-lived intervals below, which would otherwise see mount-time values.
  const subscriptionInfoRef = useRef(subscriptionInfo)
  subscriptionInfoRef.current = subscriptionInfo
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  const setPermissionsVersion = useSetAtom(permissionsVersionAtom)

  const [sideBarStateChanged, setSideBarStateChanged] = useState(0)

  const [hasSupplier, setHasSupplier] = useState(Storage.hasCookieValue(Enums.Cookie.supplierID))

  const [timeshiftMilliseconds, setTimeshiftMilliseconds, getTimeshiftMillisecondsValue] = useRefState(-1) // default to -1 to show not initialized, but wont break logic using this value

  // Usetiful load/unload controls based on MasterSystemAdmin permission
  const usetifulLoadedRef = useRef(false)

  const getUsetifulUserTags = () => {
    const userId = Storage.getCookie(Enums.Cookie.userID) || 'ANONYMOUS'
    const fullName = Storage.getCookie(Enums.Cookie.servFullName) || ''
    const emailOrUsername = Storage.getCookie(Enums.Cookie.servUserName) || ''
    let firstName = ''
    let lastName = ''
    if (fullName && typeof fullName === 'string') {
      const parts = fullName.trim().split(/\s+/)
      firstName = parts[0] || ''
      lastName = parts.slice(1).join(' ') || ''
    }
    if (!firstName && emailOrUsername) {
      firstName = emailOrUsername.split('@')[0]
    }
    return {
      userId: String(userId),
      firstName: String(firstName || ''),
      lastName: String(lastName || ''),
    }
  }

  // Derive tenant-related tags for Usetiful (trial/live status and anniversary date)
  const getUsetifulTenantTags = () => {
    try {
      const si = subscriptionInfo || Storage.getCookie(Enums.Cookie.subscriptionInfo) || {}
      // Determine trial vs live status using multiple indicators found in the codebase
      const accessStatus = si?.AccessStatus
      const customerStatus = si?.CustomerStatus || si?.CustomerStatusEnum
      const isTrial = accessStatus === Enums.AccessStatus?.Trial
        || customerStatus === 'Trial'
        || customerStatus === Enums?.CustomerStatus?.Trial

      // Pick the best available anniversary date
      const annivRaw = si?.AnniversaryDate
        || si?.FirstAnniversaryDate
        || si?.SignUpCompleteDate
        || null

      const tenantAnniversary = annivRaw ? moment(annivRaw).isValid() ? moment(annivRaw).format('YYYY-MM-DD') : null : null

      const tenantId = Storage.getCookie(Enums.Cookie.tenantID)

      return {
        tenantId: tenantId ? String(tenantId) : undefined,
        tenantStatus: isTrial ? 'trial' : 'live',
        tenantAnniversary: tenantAnniversary,
        version: __APP_VERSION__,
      }
    } catch (e) {
      return { tenantStatus: 'live' }
    }
  }

  // Combine user and tenant tags for Usetiful
  const getUsetifulCombinedTags = () => ({
    ...getUsetifulUserTags(),
    ...getUsetifulTenantTags(),
  })

  const initUsetiful = async () => {
    try {
      if (usetifulLoadedRef.current) return
      const { loadUsetifulScript, setUsetifulTags } = await import('usetiful-sdk')
      loadUsetifulScript('05e7805ce6a8ee9c1ee4ea1db7158a11')
      setUsetifulTags(getUsetifulCombinedTags())
      usetifulLoadedRef.current = true
    } catch (e) {
      console.error('Usetiful initialization error', e)
    }
  }

  const updateUsetifulTagsIfLoaded = async () => {
    try {
      if (!usetifulLoadedRef.current) return
      const { setUsetifulTags } = await import('usetiful-sdk')
      setUsetifulTags(getUsetifulCombinedTags())
    } catch (e) {
      // non-fatal
    }
  }

  // Keep Usetiful tags in sync when subscription info changes (e.g., trial -> live)
  useEffect(() => {
    // only attempt if running in browser
    if (typeof window === 'undefined') return
    updateUsetifulTagsIfLoaded()
    identifyClarityUser()
    identifySentryUser()
  }, [subscriptionInfo])

  const initUtisfulForNonAdminUsers = async () => {
    if (typeof window === 'undefined') return
    const hasEmployee = Storage.hasCookieValue(Enums.Cookie.employeeID)
    const superAdminUser = PS.hasPermission(Enums.PermissionName.MasterSystemAdmin)
    if (hasEmployee && !superAdminUser) { // logged in and user is not admin, load usetiful script
      if (!usetifulLoadedRef.current) {
        await initUsetiful()
      } else {
        await updateUsetifulTagsIfLoaded()
      }
    }
  }

  const updateTimeshift = async () => {
    const hasEmployee = Storage.hasCookieValue(Enums.Cookie.employeeID)

    if (hasEmployee) {
      var tsm = await Time.getTimeshiftMilliseconds(false)
      setTimeshiftMilliseconds((tsm as any))
    }
  }

  let mouse = useRef({ x: -1, y: -1, stamp: new Date().valueOf() })

  const updateMousePosition = (ev: any) => {
    if (mouse.current.x !== ev.clientX || mouse.current.y !== ev.clientY) {
      mouse.current = { x: ev.clientX, y: ev.clientY, stamp: new Date().valueOf() }
    }
  }

  const hubspotTimer = useRef<any>(null)
  const tokenRefreshTimer = useRef<any>(null)

  // Shares its cache with ProfileMenu's identical query, so the Show/Hide Help Chat
  // preference is only fetched once.
  const hubspotConfigQuery = useQuery(['userAuth'], () => UserConfigService.getPageFilters(Enums.ConfigurationSection.None))

  // Proactively refresh the PlatformAuth access token before its 15-minute expiry so
  // requests never eat a 401 round-trip. Cheap (a JWT decode) unless expiry is within
  // 5 minutes, in which case it fires one network call to /api/auth/refresh.
  async function checkPlatformTokenExpiry() {
    const currentAccessToken = Storage.getCookie('platformAccessToken')
    if (!currentAccessToken) return // Not a PlatformAuth session

    try {
      // Serialised against the Fetch.ts 401 path and against every other tab's heartbeat —
      // see services/platform-token-refresh. Returns null when the token still has plenty of
      // life left, and persists the new pair itself when it doesn't.
      const newTokens = await refreshPlatformSession('expiry', currentAccessToken)
      if (newTokens) {
        console.log('[AUTH] Platform access token refreshed')
      }
    } catch (err) {
      if (isSessionDenied(err)) {
        logout(true, 'Your access has changed — please log in again', 'heartbeat-refresh-denied')
      } else {
        console.warn('[AUTH] Platform token refresh failed — keeping session, will retry:', err)
      }
    }
  }

  async function refreshPermissions() {
    const hasEmployee = Storage.hasCookieValue(Enums.Cookie.employeeID)
    if (checkForInactivity(runningTimerWatchdogMinutes) || !hasEmployee) return
    const changed = await employeeService.refreshPermissions()
    if (changed) {
      setPermissionsVersion(v => v + 1)
      const requiredPerms = getRequiredPermissions(pathnameRef.current)
      if (Array.isArray(requiredPerms) && requiredPerms.length > 0 && !PS.hasPermission(requiredPerms)) {
        navigate({ to: '/' })
      }
    }
  }

  useEffect(() => {
    getSubscriptionInfo()
    updateTimeshift()

    window.addEventListener('mousemove', updateMousePosition)

    refTimer.current = setInterval(async () => {
      // Only update subscription info if it actually changed to prevent unnecessary re-renders
      if (canUpdateSubscriptionInfo()) {
        const cookieSubscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo)
        // Deep comparison to avoid unnecessary state updates
        if (JSON.stringify(subscriptionInfoRef.current) !== JSON.stringify(cookieSubscriptionInfo)) {
          setSubscriptionInfo(cookieSubscriptionInfo)
        }
      }
      await updateTimeshift()
      await authenticationService.updateAPIEndpoint()
      await refreshPermissions()
      await initUtisfulForNonAdminUsers()
    }, constants.permissionsRefreshIntervalMS)

    // Token refresh runs on its own 2-minute interval, independent of the 15-minute
    // permissions tick above.
    tokenRefreshTimer.current = setInterval(async () => {
      await checkPlatformTokenExpiry()
    }, 120_000)
    checkPlatformTokenExpiry() // run immediately on mount too

    // Browsers suspend timers in a background tab, so the heartbeat sleeps with the tab.
    // Refresh as soon as the tab is shown again, before the first API call goes out with an expired token.
    const onResume = () => {
      if (document.visibilityState === 'visible') checkPlatformTokenExpiry()
    }
    document.addEventListener('visibilitychange', onResume)
    window.addEventListener('pageshow', onResume)

    refreshPermissions()

    initUtisfulForNonAdminUsers()

    clarity(window, document, 'clarity', 'script', Helper.getClarityID())

    hubspotTimer.current = setInterval(() => {
      hubspotVisibility()
    }, 100)

    return () => {
      window.removeEventListener('mousemove', updateMousePosition)
      document.removeEventListener('visibilitychange', onResume)
      window.removeEventListener('pageshow', onResume)
      clearInterval(refTimer.current)
      clearInterval(tokenRefreshTimer.current)
      clearInterval(hubspotTimer.current)
    }
  }, [])

  // Delay loading HubSpot until we know the user's Show/Hide Help Chat preference, so a
  // hidden preference means the widget is never injected in the first place instead of
  // being loaded and then raced to hide it.
  useEffect(() => {
    if (!hubspotConfigQuery.isSuccess && !hubspotConfigQuery.isError) return

    // A failed preference fetch falls back to the default (shown) rather than never loading the chat.
    const shownValue = hubspotConfigQuery.isSuccess
      ? UserConfigService.getMetaDataValue(hubspotConfigQuery.data, hubspotService.shownMetadataKey)
      : null
    let cancelled = false
    const hsDelay = 2000

    const timer = setTimeout(() => {
      if (cancelled) return
      console.log(`Loading hubspot...Start`)
      try {
        if (shownValue !== false) {
          hubspotService.loadHubspot()
        } else if (!hubspotService.isScriptInjected()) {
          // Skip if the user already chose Show Help Chat during the delay - hiding here would undo it.
          hubspotService.hideChatWidget()
        }
      } catch (error) {
        console.error(`Loading hubspot...Error ${JSON.stringify(error)}`)
      }
      console.log(`Loading hubspot...End`)
      onHsLoaded(true)
    }, hsDelay)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [hubspotConfigQuery.isSuccess, hubspotConfigQuery.isError])

  const isUnauthRoute = useMemo(() => {
    if (!pathname) return true
    const lowerPath = pathname.toLowerCase()
    let prefixPath = constants.PUBLIC_ROUTE_PREFIXES.some((prefix: any) => lowerPath.startsWith(prefix))
    return constants.PUBLIC_ROUTES_EXACT.has(lowerPath) || prefixPath
  }, [pathname])

  // Update subscription info when user logs in (route changes from /login to another page)
  useEffect(() => {
    if (!isUnauthRoute) {
      // Check if cookies exist (user is authenticated)
      const token = Storage.getCookie(Enums.Cookie.token)
      if (token) {
        // Update subscription info from cookies immediately after login
        const cookieSubInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo)
        if (cookieSubInfo && JSON.stringify(cookieSubInfo) !== JSON.stringify(subscriptionInfo)) {
          console.log('Updating subscription info after route change')
          setSubscriptionInfo(cookieSubInfo)
        }
      }
    }
  }, [isUnauthRoute])

  const hubspotActive = useRef(false)
  const hubspotSize = useRef<any>(null)

  const [hsLoaded, onHsLoaded] = useState(false)

  const setHubspotActive = (val: any) => {
    hubspotActive.current = val
  }

  const hubspotVisibility = () => {
    let elem = document.getElementById('hubspot-messages-iframe-container')
    if (!elem) return

    if (!elem.style) {
      return
    }

    let height = parseInt(elem.style.height.replace('px', ''))

    if (hubspotSize.current === null) {
      hubspotSize.current = height
    }

    if (hubspotActive.current === true && elem.style.display === 'none') {
      elem.style.setProperty('display', 'block', 'important')
      hubspotService.reInitialiseChatWidget()
    } else if (hubspotActive.current === true
      && elem.style.display !== 'none'
      && hubspotSize.current && hubspotSize.current === height) {

      window.HubSpotConversations.widget.open()
      hubspotActive.current = false
    }

    if (hubspotSize.current > height) {
      let fromLogin = sessionStorage.getItem('hubspotFromLogin')
      if (fromLogin && fromLogin === 'true') {
        sessionStorage.setItem('hubspotFromLogin', 'false')
        hubspotService.reInitialiseChatWidget()
      }
    }

    hubspotSize.current = height
  }

  const canUpdateSubscriptionInfo = () => {
    const cookie = Storage.getCookie(Enums.Cookie.subscriptionInfo)
    const current = subscriptionInfoRef.current
    if (cookie && !current) {
      return true
    } else if (cookie && current) {
      return JSON.stringify(cookie) !== JSON.stringify(current)
    } else {
      return false
    }
  }

  const autoDismissMaintenanceSchedule = (newMaintenanceSchedule: any) => {
    if (newMaintenanceSchedule && newMaintenanceSchedule.showMaintenanceWarning) {
      let dismissedId = Storage.getCookie(Enums.Cookie.maintenanceMessageDismissed)
      // FYI if you want to NOT show the message on specific pages, auto dismiss here by checking the pathname
      if (dismissedId === newMaintenanceSchedule.id) {
        newMaintenanceSchedule.showMaintenanceWarning = false
      }
    }
    return newMaintenanceSchedule
  }

  const setMaintenanceShedule = (newMaintenanceSchedule: any) => {
    currentMaintenanceScheduleRef.current = newMaintenanceSchedule
    setCurrentMaintenanceSchedule(() => newMaintenanceSchedule) // used to trigger a rerender
  }

  const breakpoint = useBreakpoint()
  Storage.setCookie(Enums.Cookie.breakpoint, breakpoint)

  const checkForInactivity = (watchdogMinutes: any) => {
    let inactive = false
    let newStamp = new Date().valueOf()

    if (newStamp > mouse.current.stamp + (1000 * 60 * watchdogMinutes)) {
      inactive = true
    }

    return inactive
  }

  const clarity = (c: any, l: any, a: any, r: any, i: any) => {
    if (!i) return
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
    let t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i
    let y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y)

    setTimeout(() => {
      identifyClarityUser()
    }, 2000)
  }

  const [appointmentPage, setAppointmentPage] = useState('/appointment/scheduler')

  const mobileView = useMediaQuery('(max-device-width: 1023px)')

  const [mobileSidebarExpanded, setMobileSidebarExpanded] = useState(false)
  const [tenantName, setTenantName] = useState('')
  const updateTenantName = (newName: any) => {
    if (newName !== tenantName) {
      setTenantName(newName)
    }
  }

  const [currentMaintenanceSchedule, setCurrentMaintenanceSchedule] = useState(null)
  const currentMaintenanceScheduleRef = useRef(null)

  const dismissMaintenanceSchedule = () => {
    Storage.setCookie(Enums.Cookie.maintenanceMessageDismissed, currentMaintenanceScheduleRef.current.id)
    setMaintenanceShedule({ ...currentMaintenanceScheduleRef.current, showMaintenanceWarning: false })
  }

  if (currentMaintenanceScheduleRef.current?.showMaintenanceMessage) {
    return (
      <div style={{
        display: 'flex',
        margin: 0,
        padding: 0,
        height: '100vh',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
        }}>
          <div style={{
            backgroundColor: colors.background,
            borderTopLeftRadius: layout.bodyRadius,
            flexGrow: 1,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            padding: '1rem',
            textAlign: 'center',
          }}>
            <img src="server-maintenance.svg" style={{
              position: 'fixed',
              right: '0.5rem',
              bottom: '0.5rem',
              opacity: 0.2,
              height: '4rem',
            }} />
            <div style={{
              marginTop: '50vh',
              transform: 'translateY(-50%)',
            }}>
              <img src="/logo-type-blue.svg" />
              <h4>{currentMaintenanceScheduleRef.current.maintenanceMessage}</h4>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ThemeProvider theme={themeOptions}>
      <HubspotContext.Provider value={{ setIsActive: setHubspotActive, loaded: hsLoaded }}>
        <BusyIndicatorContext.Provider value={{ setText: setBusyIndicatorText }}>
            <AuthenticatedInner
              appointmentPage={appointmentPage}
              setAppointmentPage={setAppointmentPage}
              mobileView={mobileView}
              mobileSidebarExpanded={mobileSidebarExpanded}
              setMobileSidebarExpanded={setMobileSidebarExpanded}
              tenantName={tenantName}
              updateTenantName={updateTenantName}
              timeshiftMilliseconds={timeshiftMilliseconds}
              setTimeshiftMilliseconds={setTimeshiftMilliseconds}
              updateTimeshift={updateTimeshift}
              usingNewComponents={usingNewComponents}
              sideBarStateChanged={sideBarStateChanged}
              setSideBarStateChanged={setSideBarStateChanged}
              confirmOptions={confirmOptions}
              setConfirmOptions={setConfirmOptions}
              busyIndicatorText={busyIndicatorText}
              pathname={pathname}
            />
        </BusyIndicatorContext.Provider>
      </HubspotContext.Provider>
    </ThemeProvider>
  )
}

function AuthenticatedInner(p) {
  return (
    <PageContext.Provider value={{
      appointmentPage: p.appointmentPage, setAppointmentPage: p.setAppointmentPage,
      mobileView: p.mobileView, mobileSidebarExpanded: p.mobileSidebarExpanded,
      setMobileSidebarExpanded: p.setMobileSidebarExpanded, tenantName: p.tenantName,
      updateTenantName: p.updateTenantName, customerZone: false, tenantZone: false,
      timeshiftMilliseconds: p.timeshiftMilliseconds, setTimeshiftMilliseconds: p.setTimeshiftMilliseconds,
      updateTimeshift: p.updateTimeshift, usingNewComponents: p.usingNewComponents,
      sideBarStateChanged: p.sideBarStateChanged, setSideBarStateChanged: p.setSideBarStateChanged,
    }}>
      <AuthProvider>
        <Outlet key={p.pathname} />
      </AuthProvider>
      <ConfirmAction options={p.confirmOptions} setOptions={p.setConfirmOptions} />
      <BusyIndicator text={p.busyIndicatorText} />
    </PageContext.Provider>
  )
}
